import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  Bell,
  Check,
  CheckCheck,
  ExternalLink,
  Loader2,
  MessagesSquare,
  Send,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import { Fullbleed } from '@/contexts/fullbleed';
import {
  useArchiveNotification,
  useCreateNotificationBroadcast,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsList,
  useNotificationUnreadQuery,
} from '@/hooks/notifications/useStaffNotifications';
import { formatDateTimeInTimeZone, getViewerTimeZone } from '@/lib/date-values';
import {
  INTERNAL_MESSAGE_NOTIFICATION_TYPE,
  notificationActionUrl,
} from '@/lib/internal-chat-notification-target';
import { useAuthPermissions } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import {
  NOTIFICATION_PRIORITY_META,
  type NotificationCategory,
  type NotificationPriority,
  type NotificationStatusFilter,
  type StaffNotification,
} from '@/types/notifications';

type CategoryFilter = 'all' | NotificationCategory;
type PriorityFilter = 'all' | NotificationPriority;
type NotificationSection = 'notifications' | 'messages';

const CATEGORY_FILTERS: readonly { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'leads', label: 'Leads' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'billing', label: 'Billing' },
  { value: 'sales', label: 'Sales' },
  { value: 'jobs', label: 'Jobs' },
  { value: 'team', label: 'Team' },
  { value: 'system', label: 'System' },
];

const PRIORITY_FILTERS: readonly { value: PriorityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'action', label: 'Needs action' },
  { value: 'attention', label: 'Review' },
  { value: 'fyi', label: 'FYI' },
];

const STATUS_FILTERS: readonly { value: NotificationStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
  { value: 'archived', label: 'Archived' },
];

const categoryLabels: Record<NotificationCategory, string> = {
  leads: 'Leads',
  scheduling: 'Scheduling',
  contacts: 'Contacts',
  billing: 'Billing',
  sales: 'Sales',
  jobs: 'Jobs',
  team: 'Team',
  system: 'System',
};

type DateGroupKey = 'today' | 'yesterday' | 'this-week' | 'before';

const DATE_GROUP_ORDER: readonly DateGroupKey[] = ['today', 'yesterday', 'this-week', 'before'];
const DATE_GROUP_LABELS: Record<DateGroupKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  'this-week': 'This week',
  before: 'Earlier',
};

export const Route = createFileRoute('/_dashboard/dashboard/notifications')({
  component: NotificationsPage,
});

function NotificationsPage() {
  const navigate = useNavigate();
  const permissions = useAuthPermissions();
  const canBroadcast = permissions.includes('notifications:manage');
  const [section, setSection] = useState<NotificationSection>('notifications');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<NotificationStatusFilter>('unread');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const listQuery = useNotificationsList({
    category: section === 'notifications' ? categoryFilter : undefined,
    priority: section === 'notifications' ? priorityFilter : undefined,
    eventType: section === 'messages' ? INTERNAL_MESSAGE_NOTIFICATION_TYPE : undefined,
    excludeEventType: section === 'notifications' ? INTERNAL_MESSAGE_NOTIFICATION_TYPE : undefined,
    status: statusFilter,
  });
  const unreadQuery = useNotificationUnreadQuery();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const archiveNotification = useArchiveNotification();
  const items = listQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const unreadCount = unreadQuery.data?.count ?? 0;
  const viewerTimeZone = getViewerTimeZone();
  const sections = useMemo(
    () => groupNotificationsByDate(items, viewerTimeZone),
    [items, viewerTimeZone],
  );
  const visibleStatus = statusFilter === 'unread' || statusFilter === 'all' ? statusFilter : null;
  const hasActiveFilters =
    (section === 'notifications' && (categoryFilter !== 'all' || priorityFilter !== 'all')) ||
    statusFilter !== 'unread';
  const activeHiddenFilterCount =
    (categoryFilter !== 'all' ? 1 : 0) +
    (priorityFilter !== 'all' ? 1 : 0) +
    (statusFilter === 'read' || statusFilter === 'archived' ? 1 : 0);
  const resetFilters = () => {
    setCategoryFilter('all');
    setPriorityFilter('all');
    setStatusFilter('unread');
  };

  return (
    <>
      <Fullbleed />
      <div className="page-enter flex h-full min-h-0 flex-col p-3 sm:p-4">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="flex flex-col gap-3 border-b px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <h1 className="truncate text-base font-semibold">Notifications</h1>
                {unreadCount > 0 ? (
                  <Badge variant="secondary" className="shrink-0">
                    {unreadCount} unread
                  </Badge>
                ) : null}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="grid h-8 grid-cols-2 rounded-md border bg-muted/50 p-1">
                  {[
                    ['unread', 'Unread'],
                    ['all', 'All'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setStatusFilter(value as NotificationStatusFilter)}
                      className={cn(
                        'rounded-sm px-3 text-xs font-semibold transition',
                        visibleStatus === value
                          ? 'bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground',
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {section === 'notifications' ? (
                  <NotificationFiltersPopover
                    open={filtersOpen}
                    onOpenChange={setFiltersOpen}
                    categoryFilter={categoryFilter}
                    priorityFilter={priorityFilter}
                    statusFilter={statusFilter}
                    activeFilterCount={activeHiddenFilterCount}
                    onCategoryChange={setCategoryFilter}
                    onPriorityChange={setPriorityFilter}
                    onStatusChange={setStatusFilter}
                    onReset={resetFilters}
                  />
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 justify-center"
                  onClick={() => markAll.mutate()}
                  disabled={unreadCount === 0 || markAll.isPending}
                >
                  {markAll.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <CheckCheck className="size-3.5" />
                  )}
                  Mark all read
                </Button>
                {canBroadcast ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 justify-center"
                    onClick={() => setBroadcastOpen(true)}
                  >
                    <Send className="size-3.5" />
                    Broadcast
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-5" aria-label="Notification sections">
              {(
                [
                  ['notifications', Bell, 'Notifications'],
                  ['messages', MessagesSquare, 'Messages'],
                ] as const
              ).map(([value, Icon, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={section === value}
                  onClick={() => setSection(value)}
                  className={cn(
                    'relative inline-flex items-center gap-1.5 pb-1.5 text-sm font-semibold transition-colors',
                    section === value
                      ? 'text-foreground after:absolute after:inset-x-0 after:-bottom-3 after:h-0.5 after:bg-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {listQuery.isLoading ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 size-4 animate-spin" />
              Loading notifications
            </div>
          ) : listQuery.isError ? (
            <div className="flex flex-1 items-center justify-center px-6 text-center">
              <div className="max-w-xs space-y-3">
                <p className="text-sm font-medium">Notifications could not load</p>
                <p className="text-xs text-muted-foreground">
                  Retry when the connection is available.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void listQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-1 items-center justify-center px-6 text-center">
              <div className="flex max-w-xs flex-col items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground">
                  {section === 'messages' ? (
                    <MessagesSquare className="size-4" />
                  ) : (
                    <Bell className="size-4" />
                  )}
                </div>
                <p className="text-sm font-medium">
                  {section === 'messages'
                    ? statusFilter === 'unread'
                      ? 'No unread team messages'
                      : 'No team message notifications'
                    : emptyNotificationsTitle(categoryFilter, priorityFilter, statusFilter)}
                </p>
                {hasActiveFilters ? (
                  <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
                    Clear filters
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto">
              {sections.map((section) => (
                <section key={section.key} aria-label={section.label}>
                  <div className="border-b bg-muted/25 px-5 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {section.label}
                  </div>
                  {section.items.map((item) => (
                    <NotificationRow
                      key={item.recipientId}
                      item={item}
                      marking={markRead.isPending && markRead.variables === item.recipientId}
                      archiving={
                        archiveNotification.isPending &&
                        archiveNotification.variables === item.recipientId
                      }
                      onRead={() => markRead.mutate(item.recipientId)}
                      onArchive={() => archiveNotification.mutate(item.recipientId)}
                      onOpen={() => openNotificationTarget(notificationActionUrl(item), navigate)}
                    />
                  ))}
                </section>
              ))}
              {listQuery.hasNextPage ? (
                <div className="flex justify-center border-t px-4 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void listQuery.fetchNextPage()}
                    disabled={listQuery.isFetchingNextPage}
                  >
                    {listQuery.isFetchingNextPage ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : null}
                    Load more
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </section>
      </div>
      {canBroadcast ? (
        <BroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} />
      ) : null}
    </>
  );
}

function NotificationFiltersPopover({
  open,
  onOpenChange,
  categoryFilter,
  priorityFilter,
  statusFilter,
  activeFilterCount,
  onCategoryChange,
  onPriorityChange,
  onStatusChange,
  onReset,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryFilter: CategoryFilter;
  priorityFilter: PriorityFilter;
  statusFilter: NotificationStatusFilter;
  activeFilterCount: number;
  onCategoryChange: (value: CategoryFilter) => void;
  onPriorityChange: (value: PriorityFilter) => void;
  onStatusChange: (value: NotificationStatusFilter) => void;
  onReset: () => void;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-8 justify-center">
          <SlidersHorizontal className="size-3.5" />
          {activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[92vw] max-w-[460px] space-y-5 p-4">
        <FilterGroup label="Category">
          {CATEGORY_FILTERS.map((filter) => (
            <FilterChip
              key={filter.value}
              selected={categoryFilter === filter.value}
              onClick={() => onCategoryChange(filter.value)}
            >
              {filter.label}
            </FilterChip>
          ))}
        </FilterGroup>

        <FilterGroup label="Priority">
          {PRIORITY_FILTERS.map((filter) => (
            <FilterChip
              key={filter.value}
              selected={priorityFilter === filter.value}
              onClick={() => onPriorityChange(filter.value)}
            >
              {filter.label}
            </FilterChip>
          ))}
        </FilterGroup>

        <FilterGroup label="Status">
          {(['unread', 'read', 'archived'] as const).map((value) => (
            <FilterChip
              key={value}
              selected={statusFilter === value}
              onClick={() => onStatusChange(value)}
            >
              {STATUS_FILTERS.find((filter) => filter.value === value)?.label}
            </FilterChip>
          ))}
        </FilterGroup>

        <div className="flex items-center justify-end border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={onReset}
            disabled={
              categoryFilter === 'all' && priorityFilter === 'all' && statusFilter === 'unread'
            }
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center justify-center rounded-full border px-3.5 text-xs font-semibold transition',
        selected
          ? 'border-foreground bg-foreground text-background'
          : 'border-border bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function NotificationRow({
  item,
  marking,
  archiving,
  onRead,
  onArchive,
  onOpen,
}: {
  item: StaffNotification;
  marking: boolean;
  archiving: boolean;
  onRead: () => void;
  onArchive: () => void;
  onOpen: () => void;
}) {
  const archived = item.archivedAt !== null;
  const unread = item.readAt === null && !archived;
  const hasSafeAction = isSafeNotificationActionUrl(item.actionUrl);
  const meta = NOTIFICATION_PRIORITY_META[item.priority];
  const titleClass = item.priority === 'fyi' ? 'font-medium' : 'font-semibold';
  return (
    <div
      className={cn(
        'group/notification grid grid-cols-[3px_minmax(0,1fr)_auto] gap-4 border-b last:border-b-0 hover:bg-muted/20',
        unread ? 'bg-muted/30' : meta.rowClass,
      )}
    >
      <span className={cn('h-full w-[3px]', meta.railClass)} />
      <button type="button" className="min-w-0 py-3 pr-0 text-left" onClick={onOpen}>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {unread ? <span className={cn('size-2 shrink-0 rounded-full', meta.dotClass)} /> : null}
          <p className={cn('min-w-0 flex-1 truncate text-sm', titleClass)}>{item.title}</p>
          {item.groupCount > 1 ? (
            <Badge variant="outline" className="shrink-0 font-bold">
              ×{item.groupCount}
            </Badge>
          ) : null}
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatNotificationWhen(item)}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.body}</p>
        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline" className={cn('font-semibold', meta.pillClass)}>
            {meta.pillLabel}
          </Badge>
          <span>{categoryLabels[item.category]}</span>
        </div>
      </button>
      <div className="flex items-start gap-1 py-3 pr-4">
        {hasSafeAction ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={onOpen}
                  aria-label="Open notification"
                >
                  <ExternalLink className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open notification</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {unread ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={onRead}
                  disabled={marking}
                  aria-label="Mark notification as read"
                >
                  {marking ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Check className="size-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark as read</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
        {!archived ? (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={onArchive}
                  disabled={archiving}
                  aria-label="Archive notification"
                >
                  {archiving ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Archive className="size-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Archive</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
    </div>
  );
}

function emptyNotificationsTitle(
  categoryFilter: CategoryFilter,
  priorityFilter: PriorityFilter,
  statusFilter: NotificationStatusFilter,
): string {
  const statusLabel =
    statusFilter === 'all'
      ? ''
      : `${STATUS_FILTERS.find((filter) => filter.value === statusFilter)?.label} `;
  const categoryLabel =
    categoryFilter === 'all' ? '' : `${categoryLabels[categoryFilter].toLowerCase()} `;
  if (priorityFilter !== 'all') {
    const priorityLabel =
      PRIORITY_FILTERS.find((filter) => filter.value === priorityFilter)?.label.toLowerCase() ??
      'priority';
    return `No ${statusLabel.toLowerCase()}${priorityLabel} ${categoryLabel}notifications`;
  }
  return `No ${statusLabel.toLowerCase()}${categoryLabel}notifications`;
}

function groupNotificationsByDate(
  items: readonly StaffNotification[],
  timeZone: string,
): Array<{ key: DateGroupKey; label: string; items: StaffNotification[] }> {
  const groups = new Map<DateGroupKey, StaffNotification[]>(
    DATE_GROUP_ORDER.map((key) => [key, []]),
  );

  for (const item of items) {
    const key = getDateGroupKey(item.createdAt, timeZone);
    groups.get(key)?.push(item);
  }

  return DATE_GROUP_ORDER.flatMap((key) => {
    const groupItems = groups.get(key) ?? [];
    return groupItems.length > 0 ? [{ key, label: DATE_GROUP_LABELS[key], items: groupItems }] : [];
  });
}

function getDateGroupKey(value: string, timeZone: string): DateGroupKey {
  const today = getViewerDateParts(new Date(), timeZone);
  const itemDate = getViewerDateParts(new Date(value), timeZone);
  if (!today || !itemDate) return 'before';

  const diffDays = datePartsToDayNumber(today) - datePartsToDayNumber(itemDate);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays <= 6) return 'this-week';
  return 'before';
}

function getViewerDateParts(
  value: Date,
  timeZone: string,
): { year: number; month: number; day: number } | null {
  if (Number.isNaN(value.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
}

function datePartsToDayNumber(parts: { year: number; month: number; day: number }): number {
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86_400_000);
}

function BroadcastDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const broadcast = useCreateNotificationBroadcast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [actionUrl, setActionUrl] = useState('');

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !broadcast.isPending;

  const reset = () => {
    setTitle('');
    setBody('');
    setActionUrl('');
  };

  const close = () => {
    if (!broadcast.isPending) reset();
    onOpenChange(false);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    const normalizedActionUrl = actionUrl.trim();
    if (normalizedActionUrl && !isSafeNotificationActionUrl(normalizedActionUrl)) {
      toast.error('Action URL must be a path or http(s) URL');
      return;
    }
    broadcast.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        type: 'manual.broadcast',
        ...(normalizedActionUrl ? { actionUrl: normalizedActionUrl } : {}),
      },
      {
        onSuccess: (result) => {
          toast.success(
            result.recipientCount > 0
              ? `Broadcast sent to ${result.recipientCount} staff member${result.recipientCount === 1 ? '' : 's'}`
              : 'Broadcast created with no active recipients',
          );
          reset();
          onOpenChange(false);
        },
        onError: () => toast.error('Could not send broadcast'),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !broadcast.isPending) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Broadcast notification</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-2">
            <Label htmlFor="notification-broadcast-title">Title</Label>
            <Input
              id="notification-broadcast-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={120}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notification-broadcast-body">Message</Label>
            <Textarea
              id="notification-broadcast-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={4}
              maxLength={1000}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notification-broadcast-action">Action URL</Label>
            <Input
              id="notification-broadcast-action"
              value={actionUrl}
              onChange={(event) => setActionUrl(event.target.value)}
              placeholder="/dashboard/jobs"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={broadcast.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {broadcast.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Send className="size-3.5" />
              )}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function openNotificationTarget(
  actionUrl: string | null,
  navigate: ReturnType<typeof useNavigate>,
): void {
  if (!actionUrl) return;
  if (!isSafeNotificationActionUrl(actionUrl)) {
    toast.error('Could not open unsafe notification link');
    return;
  }
  if (actionUrl.startsWith('/')) {
    void navigate({ to: actionUrl as never });
    return;
  }
  try {
    const url = new URL(actionUrl);
    if (url.origin === window.location.origin) {
      void navigate({ to: `${url.pathname}${url.search}${url.hash}` as never });
      return;
    }
  } catch {
    // Leave malformed external targets to the browser.
  }
  window.location.assign(actionUrl);
}

function isSafeNotificationActionUrl(actionUrl: string | null | undefined): actionUrl is string {
  if (!actionUrl) return false;
  if (actionUrl.startsWith('/')) return !actionUrl.startsWith('//');
  try {
    const url = new URL(actionUrl);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return formatDateTimeInTimeZone(value, getViewerTimeZone(), {
    month: 'short',
    day: 'numeric',
  });
}

function formatNotificationWhen(item: StaffNotification): string {
  if (item.groupCount <= 1) return formatWhen(item.createdAt);
  const firstSeen = new Date(item.firstSeenAt);
  const lastSeen = new Date(item.lastEventAt);
  if (Number.isNaN(firstSeen.getTime()) || Number.isNaN(lastSeen.getTime())) {
    return `${item.groupCount} times`;
  }
  const timeZone = getViewerTimeZone();
  const firstDate = formatDateTimeInTimeZone(item.firstSeenAt, timeZone, {
    month: 'short',
    day: 'numeric',
  });
  const lastDate = formatDateTimeInTimeZone(item.lastEventAt, timeZone, {
    month: 'short',
    day: 'numeric',
  });
  if (firstDate !== lastDate) {
    return `${item.groupCount} times · ${firstDate}-${lastDate}`;
  }
  const relative = formatWhen(item.lastEventAt);
  return `${item.groupCount} times · last ${relative}${relative === 'now' ? '' : ' ago'}`;
}
